<template>
    <Teleport to="body" :disabled="!fullScreenBackdrop">
        <transition name="modal">
            <div v-if="isVisible" class="zs-dialog-backdrop">
                <div class="zs-dialog" :style="dialogStyle" :class="dialogTypeClass">
                    <div class="zs-dialog-header">
                        <slot name="header">
                            <div class="header-content">
                                <ZSIcon v-if="dialogIcon" :icon="dialogIcon" size="x-large" class="dialog-type-icon" />
                                <div class="dialog-title">{{ displayTitle }}</div>
                            </div>
                        </slot>
                        <slot v-if="state.closeButton" name="close">
                            <span @click="hide" class="close">
                                <ZSIcon size="" icon="CloseCircle" />
                            </span>
                        </slot>
                    </div>
                    <div class="zs-dialog-body">
                        <slot>
                            <div style="white-space: pre;" v-html="state.htmlMessage"></div>
                            <span>{{ state.message }}</span>
                        </slot>
                    <div v-if="inlineFooter" class="zs-dialog-footer">
                        <slot name="footer">
                            <template v-if="isInfoOrError">
                                <ZSButton v-if="!state.dialogHideButtons" color="primary" icon="Checkmark"
                                    icon-size="small" weight="normal" @click="onOkClicked">{{ displayConfirmLabel }}</ZSButton>
                            </template>
                            <template v-else>
                                <ZSButton v-if="!state.dialogHideCancelButton && !state.dialogHideButtons" @click="onCancelClicked" class="text"
                                    icon-size="large" icon="Close" weight="normal" color="danger">{{ displayCancelLabel }}</ZSButton>
                                <ZSButton v-if="!state.dialogHideConfirmButton  && !state.dialogHideButtons" color="danger" icon="Checkmark"
                                    icon-size="small" weight="normal" @click="onOkClicked">{{ displayConfirmLabel }}</ZSButton>
                            </template>
                        </slot>
                    </div>
                    </div>
                    <div v-if="!inlineFooter" class="zs-dialog-footer">
                        <slot name="footer">
                            <template v-if="isInfoOrError">
                                <ZSButton v-if="!state.dialogHideButtons" color="primary" icon="Checkmark"
                                    icon-size="small" weight="normal" @click="onOkClicked">{{ displayConfirmLabel }}</ZSButton>
                            </template>
                            <template v-else>
                                <ZSButton v-if="!state.dialogHideCancelButton && !state.dialogHideButtons" @click="onCancelClicked" class="text"
                                    icon-size="large" icon="Close" weight="normal" color="danger">{{ displayCancelLabel }}</ZSButton>
                                <ZSButton v-if="!state.dialogHideConfirmButton  && !state.dialogHideButtons" color="danger" icon="Checkmark"
                                    icon-size="small" weight="normal" @click="onOkClicked">{{ displayConfirmLabel }}</ZSButton>
                            </template>
                        </slot>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import ZSButton from "../ZSButton.vue"
import ZSIcon  from "../global/ZSIcon.vue"


const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    // other dialog props
    type: {
        type: String,
        default: 'base',
        validator: (value) => ['base', 'info', 'error', 'confirm', 'warning'].includes(value)
    },
    title: {
        type: String,
        default: "Title"
    },
    closable: {
        type: Boolean,
        default: true
    },
    confirmLabel: {
        type: String,
        default: "Sim" // Assuming default from original state
    },
    cancelLabel: {
        type: String,
        default: "Não" // Assuming default from original state
    },
    confirmCallback: {
        type: Function,
        default: null
    },
    cancelCallback: {
        type: Function,
        default: null
    },
    hideConfirmButton: {
        type: Boolean,
        default: false
    },
    hideCancelButton: {
        type: Boolean,
        default: false
    },
    hideButtons: {
        type: Boolean,
        default: false
    },
    width: {
        type: String,
        default: "750px"
    },
    height: {
        type: String,
        default: "auto"
    },
    inlineFooter: {
        type: Boolean,
        default: false
    },
    fullScreenBackdrop: {
        type: Boolean,
        default: true
    }
});


// Refs for overrides set by the show() method
const overrideTitle = ref(null);
const overrideConfirmLabel = ref(null);
const overrideCancelLabel = ref(null);
const overrideHtmlMessage = ref(null); // Added for consistency if message needs override

const isVisible = ref(false);
const state = reactive({
    message: "",
    closeButton: props.closable,
    htmlMessage: "",
    dialogConfirmCallback: props.confirmCallback,
    dialogCancelCallback: props.cancelCallback,
    dialogHideConfirmButton: props.hideConfirmButton,
    dialogHideCancelButton: props.hideCancelButton,
    dialogHideButtons: props.hideButtons, // Added for consistency
    dialogWidth: props.width,
    dialogHeight: props.height,
    dialogType: props.type
});

const isInfoOrError = computed(() => ['info', 'error'].includes(state.dialogType));

const dialogIcon = computed(() => {
    switch (props.type) {
        case 'info':
            return 'Info';
        case 'error':
            return 'AlertOctagon';
        case 'confirm':
            return 'CheckmarkCircle';
        case 'warning':
            return 'WarningTriangle';
        default:
            return null;
    }
});

const dialogTypeClass = computed(() => {
    return props.type !== 'base' ? `zs-dialog--${props.type}` : '';
});

// Computed properties for display values
const displayTitle = computed(() => overrideTitle.value ?? props.title);
const displayConfirmLabel = computed(() => {
    if (isInfoOrError.value) {
        return overrideConfirmLabel.value ?? "OK";
    }
    return overrideConfirmLabel.value ?? props.confirmLabel;
});
const displayCancelLabel = computed(() => overrideCancelLabel.value ?? props.cancelLabel);

const dialogStyle = computed(() => ({
    width: state.dialogWidth,
    height: state.dialogHeight
}));

const onOkClicked = () => {
   const v = state.dialogConfirmCallback?.();
   
    if (typeof v === 'boolean' && v === false) {
        return;
    } 
    hide();
};

const onCancelClicked = () => {
    const v  = state.dialogCancelCallback?.();
    if (typeof v === 'boolean' && v === false) {
        return;
    } 
    hide();
};

const show = (config) => {
    overrideTitle.value = config?.title ?? null; // Use null to fallback to prop
    overrideConfirmLabel.value = config?.confirmLabel ?? null;
    overrideCancelLabel.value = config?.cancelLabel ?? null;
    overrideHtmlMessage.value = config?.htmlMessage ?? config?.message ?? null; // Handle both message types

    state.closeButton = config?.closeable ?? props.closable; // Use ?? for boolean
    state.dialogConfirmCallback = config?.confirmCallback ?? props.confirmCallback;
    state.dialogCancelCallback = config?.cancelCallback ?? props.cancelCallback;
    state.dialogHideConfirmButton = config?.hideConfirmButton ?? props.hideConfirmButton;
    state.dialogHideCancelButton = config?.hideCancelButton ?? props.hideCancelButton;
    state.dialogWidth = config?.width ?? props.width;
    state.dialogHeight = config?.height ?? props.height;
    state.dialogHideButtons = config?.hideButtons ?? props.hideButtons; // Added for consistency

    // Reset non-overridden message state if necessary
    state.htmlMessage = overrideHtmlMessage.value === null ? "" : overrideHtmlMessage.value; // Or handle based on props if applicable
    state.message = ""; // Clear plain message if htmlMessage is used
    state.dialogType = config?.type ?? props.type;

    isVisible.value = true;
};

const hide = () => {
    isVisible.value = false;
    // Emit close event
    emit('close');
    // Reset overrides when hiding
    overrideTitle.value = null;
    overrideConfirmLabel.value = null;
    overrideCancelLabel.value = null;
    overrideHtmlMessage.value = null;
};

defineExpose({
    show,
    hide
});

// Define emits for v-model:visible and close event
const emit = defineEmits(['update:visible', 'close']);
// Sync prop -> internal state
watch(() => props.visible, val => {
    isVisible.value = val;
});
// Sync internal state -> prop
watch(isVisible, val => {
    emit('update:visible', val);
});

// Watch for width and height prop changes and update state
watch(() => props.width, (newWidth) => {
    state.dialogWidth = newWidth;
});

watch(() => props.height, (newHeight) => {
    state.dialogHeight = newHeight;
});

// Watch for button visibility prop changes and update state
watch(() => props.hideConfirmButton, (newValue) => {
    state.dialogHideConfirmButton = newValue;
});

watch(() => props.hideCancelButton, (newValue) => {
    state.dialogHideCancelButton = newValue;
});

watch(() => props.hideButtons, (newValue) => {
    state.dialogHideButtons = newValue;
});
</script>

<style >
/* ZSDialog CSS Variables */
:root {
    /* Dialog Backdrop */
    --zs-dialog-backdrop-position: fixed;
    --zs-dialog-backdrop-height: 100vh;
    --zs-dialog-backdrop-bg-color: rgba(0, 0, 0, 0.5);
    --zs-dialog-backdrop-z-index: 200000000;

    /* Dialog Container */
    --zs-dialog-font-family: "Poppins", sans-serif;
    --zs-dialog-bg-color: white;
    --zs-dialog-margin: 80px auto auto;
    --zs-dialog-border-radius: 10px;
    --zs-dialog-box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    --zs-dialog-z-index: 200000001;

    /* Dialog Header */
    --zs-dialog-header-height: auto;
    --zs-dialog-header-padding: 20px 20px 10px;
    --zs-dialog-header-border-radius: 10px 10px 0 0;

    /* Dialog Title */
    --zs-dialog-title-font-family: "Poppins", sans-serif;
    --zs-dialog-title-font-size: 1.6rem;
    --zs-dialog-title-color: #333;
    --zs-dialog-title-border-bottom: 1px solid #eee;
    --zs-dialog-title-padding-bottom: 10px;

    /* Dialog Close Button */
    --zs-dialog-close-cursor: pointer;
    --zs-dialog-close-font-size: 1.5rem;
    --zs-dialog-close-color: #999;

    /* Dialog Body */
    --zs-dialog-body-padding: 20px;
    --zs-dialog-body-color: #333;
    --zs-dialog-body-font-family: "Poppins", sans-serif;
    --zs-dialog-body-font-size: 1rem;
    --zs-dialog-body-max-height: 65vh;

    /* Dialog Footer */
    --zs-dialog-footer-padding: 10px 20px 10px;
    --zs-dialog-footer-gap: 33%;

    /* Dialog Animation */
    --zs-dialog-transition-duration: 0.3s;
    --zs-dialog-transition-timing: ease;
    --zs-dialog-enter-from-transform: translateY(-50px);
    --zs-dialog-enter-from-opacity: 0;
}

.zs-dialog-backdrop {
    position: var(--zs-dialog-backdrop-position);
    top: 0;
    left: 0;
    width: 100vw;
    height: var(--zs-dialog-backdrop-height);
    overflow: hidden;
    background-color: var(--zs-dialog-backdrop-bg-color);
    display: flex;
    justify-content: center;
    z-index: var(--zs-dialog-backdrop-z-index);
    /* align-items: center; */
}

.zs-dialog {
    font-family: var(--zs-dialog-font-family);
    background-color: var(--zs-dialog-bg-color);
    align-self: flex-start;
    margin: var(--zs-dialog-margin);
    border-radius: var(--zs-dialog-border-radius);
    box-shadow: var(--zs-dialog-box-shadow);
    z-index: var(--zs-dialog-z-index);
    display: flex;
    flex-direction: column;
}

.zs-dialog-header {
    background: var(--zs-dialog-bg-color);
    height: var(--zs-dialog-header-height);
    padding: var(--zs-dialog-header-padding);
    display: flex;
    align-items: center;
    border-radius: var(--zs-dialog-header-border-radius);
    justify-content: space-between;
    /* border: 0; */
    /* border-radius: 0; */
    color: white;
    font-size: var(--zs-dialog-title-font-size);
    font-family: var(--zs-dialog-font-family);
}

.header-content {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    border-bottom: var(--zs-dialog-title-border-bottom);
    padding-bottom: var(--zs-dialog-title-padding-bottom);
}

.dialog-title {
    font-family: var(--zs-dialog-title-font-family);
    font-size: var(--zs-dialog-title-font-size);
    color: var(--zs-dialog-title-color);
    font-weight: 700;
}

.close {
    cursor: var(--zs-dialog-close-cursor);
    font-size: var(--zs-dialog-close-font-size);
    color: var(--zs-dialog-close-color);
    position: relative;
    bottom:15px;
    left:5px;
}

.zs-dialog-body {
    padding: var(--zs-dialog-body-padding);
    color: var(--zs-dialog-body-color);
    font-family: var(--zs-dialog-body-font-family);
    font-size: var(--zs-dialog-body-font-size);
    max-height: var(--zs-dialog-body-max-height);
    overflow-y: auto;
    flex: 1;
    display:flex;
    flex-direction: column;
    /* background-color:red; */
}

.zs-dialog-footer {
    margin-top:auto;
    display: flex;
    justify-content: center;
    padding: var(--zs-dialog-footer-padding);
    gap: var(--zs-dialog-footer-gap);
}

.zs-dialog-footer:empty {
  padding: 0;
  margin: 0;
}

.modal-enter-active,
.modal-leave-active {
    transition: all var(--zs-dialog-transition-duration) var(--zs-dialog-transition-timing);
}

.modal-enter-from,
.modal-leave-to {
    transform: var(--zs-dialog-enter-from-transform);
    opacity: var(--zs-dialog-enter-from-opacity);
}

.modal-enter-to,
.modal-leave-from {
    transform: translateY(0);
    opacity: 1;
}

.dialog-type-icon {
    flex-shrink: 0;
}

.zs-dialog--info .dialog-type-icon {
    color: #2196F3;
}

.zs-dialog--info .zs-dialog-header {
    border-left: 5px solid #2196F3;
}

.zs-dialog--error .dialog-type-icon {
    color: #F44336;
}

.zs-dialog--error .zs-dialog-header {
    border-left: 5px solid #F44336;
}

.zs-dialog--confirm .dialog-type-icon {
    color: #4CAF50;
}

.zs-dialog--confirm .zs-dialog-header {
    border-left: 5px solid #4CAF50;
}

.zs-dialog--warning .dialog-type-icon {
    color: #FF9800;
}

.zs-dialog--warning .zs-dialog-header {
    border-left: 5px solid #FF9800;
}


</style>
